import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../app/AuthProvider";
import { useCounterparties, useCreateDeca, useUsers, useVehicles } from "../api/hooks";
import { AdrPlacard, Button, Card, Chip, ErrorNotice, Field, Input, Notice, PageHeader, Select, Textarea } from "../components/ui";
import {
  buildCreateDecaPayload,
  emptyDecaForm,
  emptyShipment,
  validateDecaForm,
  type DecaFormState,
  type PartyField,
  type ShipmentDraft,
  type VehicleField,
} from "../lib/deca-form";
import { counterpartyRoleLabel } from "../lib/format";

/**
 * Formulario manual de emisión (CLAUDE.md Fase 4).
 *
 * Envía a `POST /api/v1/internal/deca`, el **mismo** servicio interno que usa
 * la API pública: si aquí se pudiera hacer algo distinto que por la API, sería
 * un fallo, no una variante.
 *
 * Cada parte se puede elegir del catálogo o escribir a mano (alta implícita en
 * el mismo documento). El bloque ADR de cada envío aparece solo si se marca la
 * casilla, y lo que se haya escrito ahí se descarta si se desmarca.
 */

function PartyEditor({
  label,
  field,
  onChange,
  withAddressHint,
}: {
  label: string;
  field: PartyField;
  onChange: (field: PartyField) => void;
  withAddressHint?: boolean;
}) {
  const counterparties = useCounterparties();
  const options = useMemo(
    () => (counterparties.data?.items ?? []).filter((item) => item.isActive),
    [counterparties.data],
  );

  return (
    <fieldset className="rounded-md border border-ink-200 p-3 dark:border-ink-800">
      <legend className="micro-label px-1">{label}</legend>

      <div className="mb-2 flex gap-2">
        <Button
          variant={field.mode === "catalog" ? "primary" : "quiet"}
          onClick={() => onChange({ ...field, mode: "catalog" })}
        >
          Del catálogo
        </Button>
        <Button
          variant={field.mode === "manual" ? "primary" : "quiet"}
          onClick={() => onChange({ ...field, mode: "manual" })}
        >
          Escribir los datos
        </Button>
      </div>

      {field.mode === "catalog" ? (
        <Field label="Contraparte" htmlFor={`${label}-catalog`}>
          <Select
            id={`${label}-catalog`}
            value={field.counterpartyId}
            onChange={(event) => onChange({ ...field, counterpartyId: event.target.value })}
          >
            <option value="">
              {counterparties.isLoading ? "Cargando catálogo…" : "Selecciona una contraparte"}
            </option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} · {item.nif} · {counterpartyRoleLabel(item.defaultRole)}
              </option>
            ))}
          </Select>
        </Field>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre o razón social" htmlFor={`${label}-name`}>
            <Input
              id={`${label}-name`}
              value={field.detail.name}
              onChange={(event) =>
                onChange({ ...field, detail: { ...field.detail, name: event.target.value } })
              }
            />
          </Field>
          <Field label="NIF" htmlFor={`${label}-nif`}>
            <Input
              id={`${label}-nif`}
              value={field.detail.nif}
              onChange={(event) =>
                onChange({ ...field, detail: { ...field.detail, nif: event.target.value } })
              }
            />
          </Field>
          <div className="sm:col-span-2">
            <Field
              label="Dirección"
              htmlFor={`${label}-address`}
              {...(withAddressHint ? { hint: "La dirección que figurará en el documento." } : {})}
            >
              <Input
                id={`${label}-address`}
                value={field.detail.address}
                onChange={(event) =>
                  onChange({ ...field, detail: { ...field.detail, address: event.target.value } })
                }
              />
            </Field>
          </div>
          <Field label="Correo (opcional)" htmlFor={`${label}-email`}>
            <Input
              id={`${label}-email`}
              type="email"
              value={field.detail.email}
              onChange={(event) =>
                onChange({ ...field, detail: { ...field.detail, email: event.target.value } })
              }
            />
          </Field>
          <Field label="Teléfono (opcional)" htmlFor={`${label}-phone`}>
            <Input
              id={`${label}-phone`}
              value={field.detail.phone}
              onChange={(event) =>
                onChange({ ...field, detail: { ...field.detail, phone: event.target.value } })
              }
            />
          </Field>
        </div>
      )}
    </fieldset>
  );
}

function ShipmentEditor({
  index,
  draft,
  onChange,
  onRemove,
  canRemove,
}: {
  index: number;
  draft: ShipmentDraft;
  onChange: (draft: ShipmentDraft) => void;
  onRemove: () => void;
  canRemove: boolean;
}) {
  const counterparties = useCounterparties();
  const destinatarios = (counterparties.data?.items ?? []).filter(
    (item) => item.isActive && item.defaultRole === "DESTINATARIO",
  );
  // Si no hay destinatarios marcados como tales, se ofrece todo el catálogo:
  // el rol por defecto de una contraparte es orientativo, no una restricción.
  const options = destinatarios.length > 0 ? destinatarios : counterparties.data?.items ?? [];

  return (
    <fieldset className="rounded-md border border-ink-200 p-3 dark:border-ink-800">
      <legend className="micro-label px-1">
        Envío {index + 1} {draft.esADR ? <AdrPlacard /> : null}
      </legend>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Origen" htmlFor={`origen-${index}`}>
          <Input
            id={`origen-${index}`}
            value={draft.origen}
            onChange={(event) => onChange({ ...draft, origen: event.target.value })}
            placeholder="Localidad y punto de carga"
          />
        </Field>
        <Field label="Destino" htmlFor={`destino-${index}`}>
          <Input
            id={`destino-${index}`}
            value={draft.destino}
            onChange={(event) => onChange({ ...draft, destino: event.target.value })}
            placeholder="Localidad y punto de descarga"
          />
        </Field>
        <Field label="Destinatario (opcional)" htmlFor={`destinatario-${index}`}>
          <Select
            id={`destinatario-${index}`}
            value={draft.destinatarioId}
            onChange={(event) => onChange({ ...draft, destinatarioId: event.target.value })}
          >
            <option value="">Sin especificar</option>
            {options.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} · {item.nif}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Naturaleza de la mercancía" htmlFor={`naturaleza-${index}`}>
          <Input
            id={`naturaleza-${index}`}
            value={draft.naturalezaMercancia}
            onChange={(event) => onChange({ ...draft, naturalezaMercancia: event.target.value })}
          />
        </Field>
        <Field label="Bultos" htmlFor={`bultos-${index}`}>
          <Input
            id={`bultos-${index}`}
            value={draft.bultos}
            onChange={(event) => onChange({ ...draft, bultos: event.target.value })}
            placeholder="24 palés"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Peso neto (kg)" htmlFor={`peso-neto-${index}`}>
            <Input
              id={`peso-neto-${index}`}
              inputMode="decimal"
              value={draft.pesoNetoKg}
              onChange={(event) => onChange({ ...draft, pesoNetoKg: event.target.value })}
            />
          </Field>
          <Field label="Peso bruto (kg)" htmlFor={`peso-bruto-${index}`}>
            <Input
              id={`peso-bruto-${index}`}
              inputMode="decimal"
              value={draft.pesoBrutoKg}
              onChange={(event) => onChange({ ...draft, pesoBrutoKg: event.target.value })}
            />
          </Field>
        </div>
      </div>

      <Field label="Observaciones" htmlFor={`observaciones-${index}`} hint="Notas adicionales sobre el envío (opcional). Aparecerán en el PDF.">
        <Textarea
          id={`observaciones-${index}`}
          rows={2}
          value={draft.observaciones}
          onChange={(event) => onChange({ ...draft, observaciones: event.target.value })}
          placeholder="Ej.: Mercancía frágil. Entrega en muelle 3."
          maxLength={1000}
        />
      </Field>

      <div className="mt-3">
        <label className="flex items-center gap-2 text-sm text-ink-700 dark:text-ink-200">
          <input
            type="checkbox"
            checked={draft.esADR}
            onChange={(event) => onChange({ ...draft, esADR: event.target.checked })}
          />
          Mercancía peligrosa (ADR)
        </label>
      </div>

      {draft.esADR ? (
        <div className="mt-3 grid gap-3 rounded-md bg-orange-50 p-3 dark:bg-orange-950/30 sm:grid-cols-3">
          <Field label="Número ONU" htmlFor={`onu-${index}`} hint="Ej.: UN1263">
            <Input
              id={`onu-${index}`}
              value={draft.adr.numeroOnu}
              onChange={(event) => onChange({ ...draft, adr: { ...draft.adr, numeroOnu: event.target.value } })}
            />
          </Field>
          <Field label="Clase de peligro" htmlFor={`clase-${index}`} hint="Ej.: 3">
            <Input
              id={`clase-${index}`}
              value={draft.adr.clase}
              onChange={(event) => onChange({ ...draft, adr: { ...draft.adr, clase: event.target.value } })}
            />
          </Field>
          <Field label="Grupo de embalaje" htmlFor={`grupo-${index}`} hint="Ej.: III">
            <Input
              id={`grupo-${index}`}
              value={draft.adr.grupoEmbalaje}
              onChange={(event) =>
                onChange({ ...draft, adr: { ...draft.adr, grupoEmbalaje: event.target.value } })
              }
            />
          </Field>
          <div className="sm:col-span-2">
            <Field label="Denominación" htmlFor={`denominacion-${index}`}>
              <Input
                id={`denominacion-${index}`}
                value={draft.adr.denominacion}
                onChange={(event) =>
                  onChange({ ...draft, adr: { ...draft.adr, denominacion: event.target.value } })
                }
              />
            </Field>
          </div>
          <Field label="Restricción túnel" htmlFor={`tunel-${index}`} hint="Ej.: D/E">
            <Input
              id={`tunel-${index}`}
              value={draft.adr.restriccionTunel}
              onChange={(event) =>
                onChange({ ...draft, adr: { ...draft.adr, restriccionTunel: event.target.value } })
              }
            />
          </Field>
        </div>
      ) : null}

      {canRemove ? (
        <div className="mt-3">
          <Button variant="danger" onClick={onRemove}>
            Quitar este envío
          </Button>
        </div>
      ) : null}
    </fieldset>
  );
}

export function DecaCreatePage() {
  const navigate = useNavigate();
  const { activeTenantId } = useAuth();
  // Se pasa activeTenantId para que el selector de conductor solo muestre
  // los usuarios del tenant activo — sin el filtro, la API devuelve
  // conductores de todos los tenants y aparecen usuarios ajenos en el desplegable.
  const users = useUsers(activeTenantId);
  const vehicles = useVehicles();
  const create = useCreateDeca();
  const [form, setForm] = useState<DecaFormState>(() => emptyDecaForm());
  const [problems, setProblems] = useState<string[]>([]);

  const updateShipment = (index: number, draft: ShipmentDraft): void => {
    setForm((previous) => ({
      ...previous,
      shipments: previous.shipments.map((item, position) => (position === index ? draft : item)),
    }));
  };

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    const found = validateDecaForm(form);
    setProblems(found);
    if (found.length > 0) return;
    create.mutate(buildCreateDecaPayload(form), {
      onSuccess: (result) => navigate(`/deca/${result.id}`),
    });
  };

  return (
    <>
      <PageHeader
        eyebrow="Operación"
        title="Emitir un DeCA"
        description="Para la empresa que no usa la API. Al guardar se genera el PDF con el logo y los colores de su plantilla, se le asigna una URL pública con su QR y el documento queda registrado en la auditoría. El mismo camino que sigue un integrador por API."
      />

      {create.isError ? (
        <div className="mb-4">
          <ErrorNotice error={create.error} />
        </div>
      ) : null}

      {problems.length > 0 ? (
        <div className="mb-4">
          <Notice tone="danger">
            <p className="font-semibold">Revisa estos puntos antes de emitir:</p>
            <ul className="mt-1 list-disc space-y-0.5 pl-5">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          </Notice>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <h2 className="micro-label mb-3">Intervinientes</h2>
          <div className="space-y-4">
            <PartyEditor
              label="Cargador contractual"
              field={form.cargadorContractual}
              onChange={(field) => setForm((previous) => ({ ...previous, cargadorContractual: field }))}
            />
            <PartyEditor
              label="Transportista efectivo"
              field={form.transportistaEfectivo}
              onChange={(field) => setForm((previous) => ({ ...previous, transportistaEfectivo: field }))}
            />
            <PartyEditor
              label="Operador intermedio (opcional)"
              field={form.operadorIntermedio}
              onChange={(field) => setForm((previous) => ({ ...previous, operadorIntermedio: field }))}
            />
          </div>
        </Card>

        <Card>
          <h2 className="micro-label mb-3">Vehículo</h2>
          <div className="mb-2 flex flex-wrap gap-2">
            {(
              [
                { mode: "none", label: "Sin vehículo" },
                { mode: "catalog", label: "Del catálogo" },
                { mode: "manual", label: "Escribir los datos" },
              ] as Array<{ mode: VehicleField["mode"]; label: string }>
            ).map((option) => (
              <Button
                key={option.mode}
                variant={form.vehiculo.mode === option.mode ? "primary" : "quiet"}
                onClick={() =>
                  setForm((previous) => ({
                    ...previous,
                    vehiculo: { ...previous.vehiculo, mode: option.mode },
                  }))
                }
              >
                {option.label}
              </Button>
            ))}
          </div>

          {form.vehiculo.mode === "catalog" ? (
            <Field label="Vehículo" htmlFor="vehiculo-catalogo">
              <Select
                id="vehiculo-catalogo"
                value={form.vehiculo.vehicleId}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    vehiculo: { ...previous.vehiculo, vehicleId: event.target.value },
                  }))
                }
              >
                <option value="">{vehicles.isLoading ? "Cargando…" : "Selecciona un vehículo"}</option>
                {(vehicles.data?.items ?? []).filter((item) => item.isActive).map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.plateTractor}
                    {item.plateTrailer ? ` / ${item.plateTrailer}` : ""}
                  </option>
                ))}
              </Select>
            </Field>
          ) : null}

          {form.vehiculo.mode === "manual" ? (
            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Matrícula tractora" htmlFor="vehiculo-matricula">
                <Input
                  id="vehiculo-matricula"
                  value={form.vehiculo.detail.plateTractor}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      vehiculo: {
                        ...previous.vehiculo,
                        detail: { ...previous.vehiculo.detail, plateTractor: event.target.value },
                      },
                    }))
                  }
                />
              </Field>
              <Field label="MMA tractora (kg)" htmlFor="vehiculo-mma">
                <Input
                  id="vehiculo-mma"
                  inputMode="numeric"
                  value={form.vehiculo.detail.mmaTractorKg}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      vehiculo: {
                        ...previous.vehiculo,
                        detail: { ...previous.vehiculo.detail, mmaTractorKg: event.target.value },
                      },
                    }))
                  }
                />
              </Field>
              <Field label="Matrícula remolque" htmlFor="vehiculo-remolque">
                <Input
                  id="vehiculo-remolque"
                  value={form.vehiculo.detail.plateTrailer}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      vehiculo: {
                        ...previous.vehiculo,
                        detail: { ...previous.vehiculo.detail, plateTrailer: event.target.value },
                      },
                    }))
                  }
                />
              </Field>
            </div>
          ) : null}
        </Card>

        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="micro-label">Envíos</h2>
            <Button
              onClick={() =>
                setForm((previous) => ({
                  ...previous,
                  shipments: [...previous.shipments, emptyShipment()],
                }))
              }
            >
              Añadir envío
            </Button>
          </div>
          <div className="space-y-4">
            {form.shipments.map((draft, index) => (
              <ShipmentEditor
                key={index}
                index={index}
                draft={draft}
                canRemove={form.shipments.length > 1}
                onChange={(next) => updateShipment(index, next)}
                onRemove={() =>
                  setForm((previous) => ({
                    ...previous,
                    shipments: previous.shipments.filter((_, position) => position !== index),
                  }))
                }
              />
            ))}
          </div>
        </Card>

        <Card>
          <h2 className="micro-label mb-3">Asignación y fechas</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field
              label="Conductor (opcional)"
              htmlFor="driver"
              hint="Se le notificará en la app y podrá consultar el documento sin cobertura."
            >
              <Select
                id="driver"
                value={form.driverId}
                onChange={(event) => setForm((previous) => ({ ...previous, driverId: event.target.value }))}
              >
                <option value="">Sin asignar</option>
                {(users.data?.items ?? [])
                  .filter((user) => user.role === "CONDUCTOR")
                  .map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.fullName}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field
              label="Inicio del transporte"
              htmlFor="transport-start"
              hint="Fecha y hora de inicio real del servicio. Independiente de cuándo se crea el documento. Aparece en el PDF."
            >
              <Input
                id="transport-start"
                type="datetime-local"
                value={form.transportStartDate}
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, transportStartDate: event.target.value }))
                }
              />
            </Field>
            <Field
              label="Fin del transporte"
              htmlFor="transport-end"
              hint="A los 7 días naturales de esta fecha se desactiva la descarga pública. El documento no se borra."
            >
              <Input
                id="transport-end"
                type="date"
                value={form.transportEndDate}
                onChange={(event) =>
                  setForm((previous) => ({ ...previous, transportEndDate: event.target.value }))
                }
              />
            </Field>
          </div>
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "Emitiendo…" : "Emitir DeCA"}
          </Button>
          <Button onClick={() => navigate("/deca")}>Cancelar</Button>
          {create.isPending ? <Chip tone="neutral">Generando el PDF y subiéndolo al almacenamiento</Chip> : null}
        </div>
      </form>
    </>
  );
}
